import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError, setAccessToken, getAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Persist the access token so subsequent requests can use the Authorization
    // header when third-party cookies are blocked (e.g. iOS Safari iframes).
    if (data?.access_token) setAccessToken(data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAccessToken(null);
    setUser(false);
  };

  const impersonate = async (targetId) => {
    const { data } = await api.post(`/admin/users/${targetId}/impersonate`);
    if (data?.access_token) setAccessToken(data.access_token);
    setUser(data);
    return data;
  };

  const stopImpersonating = async () => {
    const { data } = await api.post("/auth/stop-impersonating");
    if (data?.access_token) setAccessToken(data.access_token);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh, impersonate, stopImpersonating, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
